import { useEffect } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { VideoAsset } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import { stillProcessing } from './useCardVideoAssetPoll';
import { errText } from './videoLibraryErrorText';

/**
 * On mount/change of `attachedExternalId`, fetches its current asset and resumes polling if
 * it's still processing — so a draft-restored card picks up where it left off.
 */
export function useAttachedAssetResync({
  attachedExternalId,
  backendServiceRef,
  applyAsset,
  startPoll,
  stopPoll,
  onFailed,
}: {
  attachedExternalId?: string;
  backendServiceRef: React.MutableRefObject<BackendService>;
  applyAsset: (next: VideoAsset) => void;
  startPoll: (externalId: string) => void;
  stopPoll: () => void;
  onFailed: (message: string) => void;
}) {
  useEffect(() => {
    if (!attachedExternalId) {
      stopPoll();
      return undefined;
    }

    let cancelled = false;
    void backendServiceRef.current
      .getVideoAsset(attachedExternalId)
      .then((next) => {
        if (cancelled) return;
        applyAsset(next);
        if (stillProcessing(next.status)) startPoll(attachedExternalId);
      })
      .catch((err) => {
        if (cancelled) return;
        onFailed(errText(err));
      });

    return () => {
      cancelled = true;
    };
  }, [attachedExternalId, backendServiceRef, applyAsset, startPoll, stopPoll, onFailed]);
}
